import React, { Component } from "react";
import styled from "styled-components";
import { app } from "js/namespaces";
import { FeatureType } from "common/features";
import { DialogTitle, DialogActions, Button } from "@material-ui/core";

import { _ } from "js/vendor";
import { ds } from "js/core/models/dataService";
import { Theme } from "js/core/models/theme";
import { PREDEFINED_PALETTES } from "common/constants";
import { UIController } from "js/editor/dialogs/UIController";

import { BeautifulDialog, DialogContent, ShowConfirmationDialog } from "js/react/components/Dialogs/BaseDialog";
import { Gap30 } from "js/react/components/Gap";
import { ThumbnailGrid } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { ThemeThumbnail } from "js/editor/ThemeUI/ThemeThumbnail";
import { ScrollBox } from "js/react/components/LayoutGrid";

import "css/style-guide.scss";

const ThumbnailScrollBox = styled(ScrollBox)`
    height: 100%;
    background: white;
    width: 100%;
`;

const SimpleLabel = styled.label.attrs(({ bold }) => ({
    style: {
        fontWeight: bold ? 600 : 400
    }
}))`
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #000000;
`;

const Subtitle = styled.div`
    margin-top: 10px;

    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 125%;   
    letter-spacing: 0.5px;
    color: #222222;
`;

export class ChooseThemeDialog extends Component {
    static defaultProps = {
        title: "Select Theme",
        subtitle: null,
        selectButtonLabel: "Select",
        showHidden: false,
        preventClose: false,
        hideUserThemes: false,
        hideBuiltInThemes: false,
        showCancelButton: true,
        closeDialogOnThemeChoose: true,
        callback: null
    }

    state = {
        selectedTheme: null,
        teamThemes: [],
        userThemes: [],
        pptTheme: null
    }

    componentDidMount() {
        Promise.all([
            this.loadTeamThemes(),
            this.loadUserThemes(),
            this.loadPPTTheme()
        ])
            .then(() => {
                const { hideBuiltInThemes } = this.props;
                const { teamThemes, userThemes, pptTheme } = this.state;
                // Auto select ppt theme if there are no other options
                if (pptTheme && hideBuiltInThemes && teamThemes.length === 0 && userThemes.length === 0) {
                    this.handleChooseTheme(pptTheme);
                }
            });
    }

    setStateAsync(stateUpdates) {
        return new Promise(resolve => this.setState(stateUpdates, resolve));
    }

    async loadTeamThemes() {
        await this.setStateAsync({ teamThemes: ds.sharedThemes.where({ orgId: UIController.getOrganizationId() }) });
    }

    async loadUserThemes() {
        await this.setStateAsync({ userThemes: ds.userThemes.filter(theme => !theme.get("private")) });
    }

    async loadPPTTheme() {
        const { pptThemeModel } = this.props;
        if (pptThemeModel) {
            await this.setStateAsync({ pptTheme: new Theme(pptThemeModel, { disconnected: true, autoSync: false }) });
        }
    }

    handleSelectTheme = theme => {
        this.setState({ selectedTheme: theme });
    }

    handleChooseTheme = theme => {
        this.handleSelectTheme();

        const { callback, closeDialog, closeDialogOnThemeChoose } = this.props;

        if (!theme.has("colors")) {
            const defaultThemePalette = _.find(PREDEFINED_PALETTES, { id: theme.get("palette_name") || "colorful" });
            theme.set("colors", defaultThemePalette.colors);
        }

        if (callback) {
            callback(theme);
        }

        if (closeDialogOnThemeChoose) {
            closeDialog();
        }
    }

    handleDeleteTheme = theme => {
        ShowConfirmationDialog({
            title: "Are you sure you want to delete this theme?",
            message: "No existing presentations will be affected by this change but you won't be able to use this theme in any future presentations."
        }).then(confirmed => {
            if (confirmed) {
                theme.collection.remove([theme]);
                theme.destroy();
                this.loadUserThemes();
            }
        });
    }

    render() {
        const {
            selectedTheme,
            teamThemes,
            userThemes,
            pptTheme
        } = this.state;
        const {
            closeDialog,
            title,
            subtitle,
            selectButtonLabel,
            showHidden,
            preventClose,
            hideUserThemes,
            hideBuiltInThemes,
            showCancelButton,
            WrapperComponent = BeautifulDialog,
            styles
        } = this.props;

        const teamThemesToRender = teamThemes
            .filter(theme => showHidden || !theme.get("hidden")) // filter out hidden themes
            .sort((a, b) => a.id.localeCompare(b.id)); // lexicographic sort

        return (
            <WrapperComponent
                maxWidth="md"
                preventClose={preventClose}
                closeDialog={closeDialog}
            >
                <DialogTitle>
                    <div>{title}</div>
                    {subtitle && <Subtitle>{subtitle}</Subtitle>}
                </DialogTitle>
                <DialogContent style={styles}>
                    <ThumbnailScrollBox>
                        {
                            pptTheme &&
                            // PPT Themes are treated as custom themes. As such, they should only be available if custom themes are available.
                            app.user.features.isFeatureEnabled(FeatureType.WORKSPACE_CAN_ACCESS_CUSTOM_THEMES, app.appState.workspaceId) &&
                            <>
                                <SimpleLabel bold={!!subtitle}>YOUR POWERPOINT THEME</SimpleLabel>
                                <Gap30 />
                                <ThumbnailGrid
                                    items={[pptTheme]}
                                    colGap={30}
                                    rowGap={30}
                                    selectedItem={selectedTheme}
                                    thumbnailClass={ThemeThumbnail}
                                    onItemSelected={this.handleSelectTheme}
                                    onItemDoubleClick={this.handleChooseTheme}
                                    style={{ gridTemplateColumns: "repeat(3, 1fr)" }} // override the built-in media-query logic
                                />
                                <Gap30 />
                            </>
                        }
                        {teamThemesToRender.length > 0 && <>
                            <SimpleLabel bold={!!subtitle}>TEAM THEMES</SimpleLabel>
                            <Gap30 />
                            <ThumbnailGrid
                                items={teamThemesToRender}
                                colGap={30}
                                rowGap={30}
                                selectedItem={selectedTheme}
                                thumbnailClass={ThemeThumbnail}
                                onItemSelected={this.handleSelectTheme}
                                onItemDoubleClick={this.handleChooseTheme}
                                style={{ gridTemplateColumns: "repeat(3, 1fr)" }} // override the built-in media-query logic
                            />
                            <Gap30 />
                        </>}
                        {!hideUserThemes && userThemes.length > 0 && <>
                            <SimpleLabel bold={!!subtitle}>YOUR THEMES</SimpleLabel>
                            <Gap30 />
                            <ThumbnailGrid
                                items={userThemes}
                                colGap={30}
                                rowGap={30}
                                selectedItem={selectedTheme}
                                thumbnailClass={ThemeThumbnail}
                                onItemSelected={this.handleSelectTheme}
                                onItemDoubleClick={this.handleChooseTheme}
                                thumbnailProps={{
                                    onDeleteTheme: this.handleDeleteTheme
                                }}
                                style={{ gridTemplateColumns: "repeat(3, 1fr)" }} // override the built-in media-query logic
                            />
                            <Gap30 />
                        </>}
                        {!hideBuiltInThemes && <>
                            <SimpleLabel bold={!!subtitle}>BUILT-IN THEMES</SimpleLabel>
                            <Gap30 />
                            <ThumbnailGrid
                                items={ds.builtInThemes}
                                colGap={30}
                                rowGap={30}
                                selectedItem={selectedTheme}
                                thumbnailClass={ThemeThumbnail}
                                onItemSelected={this.handleSelectTheme}
                                onItemDoubleClick={this.handleChooseTheme}
                                style={{ gridTemplateColumns: "repeat(3, 1fr)" }} // override the built-in media-query logic
                            />
                        </>}
                    </ThumbnailScrollBox>
                </DialogContent>
                <DialogActions>
                    {showCancelButton && <Button onClick={closeDialog}>Cancel</Button>}
                    <Button
                        color="primary"
                        onClick={() => this.handleChooseTheme(selectedTheme)}
                        disabled={!selectedTheme}
                    >
                        {selectButtonLabel}
                    </Button>
                </DialogActions>
            </WrapperComponent>
        );
    }
}
